/**
 * MultiSelect
 * Transforms <select class="multi-select"> into
 * a tag-based multi-select field with dropdown suggestions.
 */

const MultiSelect = {
  /** Initialize all matching multi-select inputs */
  init() {
    document.querySelectorAll(".multi-select").forEach(selectEl => {
      this.initMultiSelect(selectEl);
    });
  },

  /** Initialize a single input-based multi-select field */
  initMultiSelect(selectEl) {
    // Extract initial values as potential suggestions (not selected)
    const allKnownValues = Array.from(selectEl.options).map(opt => ({
      value: opt.value.trim(),
      label: opt.text.trim()
    }));

    let selectedOptions = [];

    selectEl.style.display = "none";
    selectEl.removeAttribute("required");

    // === Create wrapper ===
    const wrapper_html = this.createWrapper(selectEl.id);

    // Hide original input visually
    selectEl.insertAdjacentHTML("beforebegin", wrapper_html);

    const wrapper = document.querySelector(`.multi-select-container.select-id-${selectEl.id}`);
    const tagsContainer = wrapper.querySelector(".multi-select-tags");
    const selectInput = wrapper.querySelector(".multi-select-input");
    const dropdown = wrapper.querySelector(".multi-select-dropdown");

    /** ------------------------------
     *         CORE FUNCTIONS
     * ------------------------------ */

    /** Render tags visually */
    function renderTags() {
      tagsContainer.innerHTML = "";
      selectedOptions.forEach(option => {
        const tag_html = generateTagHTML(option);
        tagsContainer.insertAdjacentHTML("beforeend", tag_html);

        tagsContainer.querySelectorAll(".multi-select-tag-remove-icon").forEach(iconEl => {
          iconEl.addEventListener("click", () => removeOption(option));
        });
      });
    }

    function generateTagHTML(option) {
      return `
        <div class="multi-select-tag" data-value="${option.value}">
          <span>${option.label}</span>
          <span class="multi-select-tag-remove-icon">&times;</span>
        </div>
      `;
    }

    /** Add a new option */
    function addOption(option) {
      selectedOptions.push(option);

      // Update original select
      const optEl = Array.from(selectEl.options).find(o => o.value === option.value);
      if (optEl) optEl.selected = true;

      renderTags();
      hideDropdown();
    }

    /** Remove an option */
    function removeOption(option) {
      selectedOptions = selectedOptions.filter(o => o.value !== option.value);

      // Update original select
      const optEl = Array.from(selectEl.options).find(o => o.value === option.value);
      if (optEl) optEl.selected = false;

      renderTags();
    }

    /** Render dropdown based on filter */
    function renderDropdown(filteredOptions) {
      dropdown.innerHTML = "";

      filteredOptions.forEach(option => {
        const li = document.createElement("li");
        li.className = "multi-select-dropdown-item";
        li.textContent = option.label;
        li.dataset.value = option.value;
        dropdown.appendChild(li);
      });

      dropdown.style.display = "block";
    }

    function hideDropdown() {
      dropdown.style.display = "none";
    }

    /** ------------------------------
     *         EVENT HANDLERS
     * ------------------------------ */

    // Focus anywhere inside container focuses input
    wrapper.addEventListener("click", e => {
      if (e.target === wrapper || e.target.parentElement === wrapper || e.target.classList.contains("multi-select-tags")) {
        selectInput.focus();
      }
    });

    // Show dropdown on focus
    selectInput.addEventListener("focus", () => {
      const available = allKnownValues
        .filter(option => option.value !== '')
        .filter(option => !selectedOptions.some(sel => sel.value === option.value));

      if (available.length > 0) {
        renderDropdown(available);
      } else {
        hideDropdown();
      }
    });

    // Filter dropdown as user types
    selectInput.addEventListener("input", e => {
      const value = e.target.value.toLowerCase();

      const filtered = allKnownValues
        .filter(opt => opt.value !== "")
        .filter(opt => !selectedOptions.some(sel => sel.value === opt.value))
        .filter(opt => opt.label.toLowerCase().includes(value));

      if (filtered.length > 0) {
        renderDropdown(filtered);
      } else {
        hideDropdown();
      }
    });

    // Handle dropdown click
    dropdown.addEventListener("click", e => {
      if (e.target.tagName === "LI") {
        const selectedOption = {
          value: e.target.dataset.value.trim(),
          label: e.target.textContent.trim()
        }
        addOption(selectedOption);
        selectInput.value = "";
      }
    });

    // Hide dropdown on outside click
    document.addEventListener("click", e => {
      if (!wrapper.contains(e.target)) hideDropdown();
    });

    // Initial render
    renderTags();
  },

  /** ------------------------------
   *         HELPER FUNCTIONS
   * ------------------------------ */

  /** Creates wrapper container */
  createWrapper(selectId) {
    let html_string = `<div class="multi-select-container relative select-id-${selectId}">
      <div class="multi-select-tags flex flex-wrap gap-1"></div>
      <input type="text" class="multi-select-input" autocomplete="off" placeholder="Type or search..." />
      <ul class="multi-select-dropdown absolute bg-white border border-gray-300 dark:border-gray-700 rounded-md mt-1 max-h-40 overflow-y-auto z-10"></ul>
    </div>`;
    return html_string;
  }
};

// Auto-initialize on DOM load
document.addEventListener("DOMContentLoaded", () => MultiSelect.init());
